import React, { useState, useEffect } from 'react';
import { Category, Product } from '../types';
import { categoriesApi, productsApi } from '../lib/api';
import { mapCategory, mapProduct } from '../lib/mappers';
import { getPlaceholderImage } from '../lib/placeholders';
import { ChevronRight } from 'lucide-react';

const IMAGE_NOT_AVAILABLE = getPlaceholderImage(400, 300, 'Image not available');

export const CategoryCards: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<Record<string, Product[]>>({});

  useEffect(() => {
    const fetchData = async () => {
      const { data, error } = await categoriesApi.getAll();
      if (error) {
        console.error('Failed to load categories', error);
        return;
      }
      const mappedCategories = (data?.categories || []).map(mapCategory);
      const mainCategories = mappedCategories.filter(cat => !cat.parent_id && cat.isActive !== false);
      setCategories(mainCategories);

      // Fetch a few products per category for image variety (first 8 categories)
      const toFetch = mainCategories.slice(0, 8);
      const productsMap: Record<string, Product[]> = {};

      for (const category of toFetch) {
        try {
          const { data: productsData } = await productsApi.getAll({
            category: category.id,
            limit: 2,
          });
          if (productsData?.products) {
            productsMap[category.id] = (productsData.products || []).map(mapProduct);
          }
        } catch (err) {
          console.error(`Error fetching products for ${category.name}:`, err);
        }
      }
      setCategoryProducts(productsMap);
    };
    fetchData();
  }, []);

  const displayCategories = categories.slice(0, 8);
  if (displayCategories.length === 0) return null;

  return (
    <section className="bg-background py-14 md:py-16">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mb-8 text-center md:mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary/75">Collections</p>
          <h2 className="mt-3 text-3xl font-bold text-text md:text-4xl">
            Shop by <span className="text-primary">Category</span>
          </h2>
        </div>
        <div className="flex flex-wrap justify-center gap-6 max-w-6xl mx-auto">
          {displayCategories.map((category, index) => {
            const products = categoryProducts[category.id] || [];
            const categoryImage = category.image_url || category.image;
            const productImage = products.length > 0
              ? (products[0].image_url || products[0].images?.[0])
              : null;
            const displayImage = categoryImage || productImage || IMAGE_NOT_AVAILABLE;

            return (
              <a
                key={category.id}
                href={`/category/${category.slug || category.id}`}
                className="group block w-full max-w-[22rem] sm:w-[calc(50%-1.5rem)] md:w-[calc(33.333%-1rem)] rounded-[24px] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              >
                <div className="relative overflow-hidden rounded-[24px] border border-primary/10 bg-secondary/55 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(13,13,13,0.08)]">
                  <div className="relative aspect-[4/3] overflow-hidden bg-white/50">
                    <img
                      src={displayImage}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-out"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = IMAGE_NOT_AVAILABLE;
                      }}
                    />
                  </div>
                  <div className="p-5 text-center">
                    <h3 className="flex min-h-[2.25rem] items-center justify-center text-xs font-semibold uppercase tracking-[0.2em] text-text transition-colors md:text-sm">
                      {category.name}
                    </h3>
                    <span className="btn-primary mt-3 inline-flex w-full items-center justify-center gap-1.5 px-3 py-2.5 text-xs md:text-sm">
                      Shop now
                      <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
        {categories.length > 8 && (
          <div className="flex justify-center mt-6 md:mt-8">
            <a
              href="/categories"
              className="btn-secondary px-6 py-2.5"
            >
              View All Categories
            </a>
          </div>
        )}
      </div>
    </section>
  );
};
